/**
 * AppRegistration — ONE reusable shared-credential "app registration" surface
 * (CD-BRIEF-shared-app-registration, kit 735eab65, 2026-07-24). Renders ANY
 * provider whose IntegrationDetail arrives with `sharedCredentials: true`,
 * entirely from backend data: shared credential fields → per-feature toggle
 * list (updateIntegrationFeature) → aggregated scopes panel → recent activity.
 * Zero provider-specific layout — the mock serves both `google` and
 * `microsoft` through these same components.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Btn, Card, ColorPicker, Icon, Input, Pill, T, Toggle, tx } from "@v0y4ge/jrni-ui";
import { getIntegrationDetail, updateIntegrationConfig, updateIntegrationFeature } from "@v0y4ge/api-client";
import { getGraphLogSettings, updateGraphLogSettings, getMicrosoftStatus, verifyMicrosoftConnection } from "./ms-graph-api";
import { ProviderLogo } from "./ProviderLogo";
import {
  PASS_COLOR_KEYS, PASS_CONTRAST_MIN, PASS_STYLE_DEFAULTS, isPassHex, passContrastNotice,
  readPassAppearance, refreshConnections, resolvePassColors, scopeMeta,
  type IntegInput, type PassAppearance, type PassColorKey,
} from "./integration-configs";
import { GsuiteDirectoryPanel } from "./GsuiteDirectoryPanel";
import { FeatureCredentialUploads } from "./WalletCredentialUpload";
import { putIntegrationFeatureSetting } from "./wallet-appearance-api";
import { toast } from "../../shell/toast";
import { useT } from "../../lib/i18n";

/* Backend shapes (api-contracts integrations.ts) — tolerant reads only. */
export interface RegFeature {
  key: string; name: string; description?: string; enabled: boolean; status?: string;
  /* The stored on/off DECISION (voyage#681). `enabled` is EFFECTIVE — decision AND required
   * credentials present — so a credential-UPLOAD form must gate on THIS, or on
   * status === 'requires_input', and never on `enabled`: the form is the only way to supply the
   * credential that would make `enabled` true, so gating on it deadlocks. Anything that ACTS on the
   * feature still gates on `enabled`. */
  requestedEnabled?: boolean;
  /* Whether this feature's required configFields are all configured. */
  credentialsSatisfied?: boolean;
  /* Human twin of a snake_case machine status ('requires_input'). */
  statusLabel?: string;
  scopes?: string[]; adminConsentRequired?: boolean; icon?: string; category?: string; requiredApi?: string; ssoManagedExternally?: boolean;
  /* Feature grouping + per-feature settings (2026-08-04): a registration whose features
   * belong to distinct API products (Twilio: Programmable Messaging vs Conversations)
   * points each feature at a group, and a channel's own settings (its sender) ride on
   * the feature instead of the shared-credential card. */
  group?: string; configFields?: Record<string, unknown>[];
}
export interface RegFeatureGroup {
  key: string; name?: string; description?: string; docs?: string; configFields?: Record<string, unknown>[];
}
/* What a registration can DO — provider auth-model facts, never current state.
 * Read tolerantly: camel as the deployed backend sends, snake as api-contracts'
 * catalog schema declares; only an explicit false is a denial. */
export interface RegCapabilities {
  verify?: boolean; runNow?: boolean; configure?: boolean; oauthFlow?: boolean; apiKeyFlow?: boolean;
  run_now?: boolean; oauth_flow?: boolean; api_key_flow?: boolean;
}
export function regCap(d: RegDetail | null | undefined, camel: keyof RegCapabilities, snake: keyof RegCapabilities): boolean | null {
  const c = d?.capabilities;
  if (!c) return null;
  const v = c[camel] ?? c[snake];
  return typeof v === "boolean" ? v : null;   /* null = not stated → legacy behaviour */
}
export interface RegDetail {
  provider: string; name: string; auth?: string; desc?: string; brand?: string; icon?: string; docs?: string;
  capabilities?: RegCapabilities;
  sharedCredentials?: boolean; features?: RegFeature[]; featureGroups?: RegFeatureGroup[]; callback?: string;
  /* Provider-specific panel copy — a registration that isn't OAuth shouldn't be described
   * in OAuth language (Twilio has account resources and an inbound webhook, not scopes
   * and a redirect URI). Absent = the default copy below. */
  featuresTitle?: string; featuresSubtitle?: string; scopesTitle?: string; scopesHint?: string;
  callbackLabel?: string; callbackHint?: string;
  registrationId?: string; featureKey?: string;
  configFields?: Record<string, unknown>[]; permissions?: string[]; scopes?: string[];
  activity?: { at?: string; e?: string; occurredAt?: string; detail?: string }[];
  status?: string; needsInput?: boolean; configuration?: { status?: string; requiresInput?: boolean }; updatedAt?: string;
  /* Tolerant read — NOT a contracted field (confirmed absent from api-contracts and
   * workers/integrations on the importer side, 2026-08-12). IntegrationDetail.tsx:127 reads
   * it optionally; undefined falls through to the capability check, which is the
   * pre-existing behaviour. If the upload connection model becomes real, contract it
   * properly and tighten this type. */
  connectionModel?: string;
}

/* A registration is "requires input" (no usable shared credentials) when the
 * backend says so — right after Disconnect, or before the first connect. */
export function regRequiresInput(d: RegDetail): boolean {
  if (d.configuration && typeof d.configuration.requiresInput === "boolean") return d.configuration.requiresInput;
  if (typeof d.needsInput === "boolean") return d.needsInput;
  const s = String(d.status ?? "").toLowerCase();
  return /requires|disconnected|not.?connected/.test(s);
}

/* Unwrap a {data} envelope (the client returns either shape across ops). */
export function regFromResponse(r: unknown): RegDetail | null {
  const rec = r && typeof r === "object" && "data" in (r as Record<string, unknown>) ? (r as { data: unknown }).data : r;
  if (!rec || typeof rec !== "object") return null;
  /* Shallow-cloned so the returned record is never reference-identical to the
   * previous one (2026-08-13). Every caller feeds this straight into React state
   * (`onDetail` / `onSaved`), and a data source that mutates its record in place
   * and hands back the SAME object makes setState a no-op: React bails out, and
   * any sibling reading the record — the pass preview reading its feature's
   * configFields — keeps painting the previous values while the component that
   * re-rendered for its own reasons shows the new ones. That is exactly how
   * clearing a pass colour left the Appearance group saying "Platform default"
   * while the card still painted the cleared colour.
   *
   * A shallow clone is sufficient and deliberate: arrays that genuinely changed
   * are REPLACED by the source rather than mutated in place, so children see the
   * new arrays, and the clone supplies the one new top-level identity React
   * needs to re-render at all. */
  return { ...(rec as RegDetail) };
}

const regFeatures = (d: RegDetail): RegFeature[] => (Array.isArray(d.features) ? d.features : []);
const toInput = (f: Record<string, unknown>): IntegInput => {
  const fieldKey = typeof f.key === "string" ? f.key : (typeof f.id === "string" ? f.id : undefined);
  // The contract's config-field `type` enum has no 'date' member (REQ-050), so an
  // expiry field arrives typed 'text'. Render expiry keys with the date picker —
  // drop this coercion when the enum gains 'date'.
  const declared = typeof f.type === "string" ? f.type : "text";
  const type = (declared === "date" || (fieldKey && /_expires_at$/.test(fieldKey)) ? "date" : declared) as IntegInput["type"];
  return {
    l: String(f.l ?? f.label ?? f.key ?? "Field"),
    placeholder: typeof f.placeholder === "string" ? f.placeholder : "",
    type,
    required: !!f.required,
    filled: typeof f.filled === "boolean" ? f.filled : String(f.filled ?? f.value ?? ""),
    readonly: !!f.readonly,
    configured: !!f.configured,
    masked: !!f.masked,
    desc: typeof f.description === "string" ? f.description : (typeof f.desc === "string" ? f.desc : undefined),
    fieldKey,
  };
};

/* Status pill — tone inferred from the backend's display status string. */
function FeatureStatusPill({ status, label }: { status?: string; label?: string }) {
  if (!status) return null;
  const v = status.toLowerCase();
  /* Feature status is a MACHINE value ('requires_input'); render the served human
   * twin when there is one, else humanize rather than print the code. */
  const shown = label || (/^[a-z0-9]+(_[a-z0-9]+)+$/.test(status)
    ? status.replace(/_/g, " ").replace(/^./, (ch) => ch.toUpperCase())
    : status);
  const tone: "success" | "warn" | "danger" | "neutral" =
    /error|fail/.test(v) ? "danger"
      : /needs|pending|awaiting|requires|consent|attention|expir/.test(v) ? "warn"
        : v === "connected" || /verified|healthy|active|live/.test(v) ? "success"
          : "neutral";
  return <Pill tone={tone} dot={tone === "success"}>{shown}</Pill>;
}

/* ─── Microsoft capability layer (built 2026-07-22/23, RESTORED 2026-07-27) ──
 * The Microsoft registration is backed by two provider-specific ops the generic
 * registration contract has no equivalent for: a POST verify and a live
 * per-capability status read (kit `cd-wiring-kit-ms-app-registration-final`).
 * They render as an ADDITIVE layer on the generic surface — every other
 * provider (google…) renders exactly as it did before. */
const MS_PROVIDER = "microsoft";
// ui-config — registration feature key → capability key in getMicrosoftStatus.
const MS_FEATURE_CAPABILITY: Record<string, string> = {
  calendar: "calendar", outlook: "calendar",
  teams: "teams", msteams: "teams",
  entra: "user_sync", user_sync: "user_sync",
  email_send: "email", msmail: "email", email: "email",
};
// ui-config — verify failure copy keyed by the backend error CODE (never HTTP status).
// Keys + byte-exact English as filed in REQ-059.
const MS_VERIFY_ERRORS: Record<string, { k: string; l: string }> = {
  TOKEN_REJECTED: { k: "settings.integrationDetail.verifyTokenRejected", l: "Credentials rejected — check the client ID, client secret and tenant ID." },
  NOT_CONFIGURED: { k: "settings.integrationDetail.verifyNotConfigured", l: "Not configured — enter the app-registration credentials first." },
  RATE_LIMITED: { k: "settings.integrationDetail.verifyRateLimited", l: "Rate limited by Microsoft — try again in a moment." },
  PROVIDER_UNREACHABLE: { k: "settings.integrationDetail.verifyProviderUnreachable", l: "Microsoft couldn't be reached — try again." },
};
// ui-config — Microsoft activity grouping (operator 2026-07-22): rows are read from
// each feature's OWN backend record and grouped under these category titles.
const MS_ACTIVITY_CATEGORIES: { key: string; title: string; members: string[] }[] = [
  { key: "events", title: "Events", members: ["outlook", "msgraphlog"] },
  { key: "users", title: "Users", members: ["entra"] },
  { key: "teams", title: "Teams", members: ["msteams"] },
  { key: "mailsSent", title: "Mails sent", members: ["msmail"] },
];

interface MsCapability { enabled?: boolean; state?: string; note?: string | null; error?: { message?: string } | null; last_verified_at?: string | null }
interface MsVerifyResult { ok?: boolean; latency_ms?: number; account?: { label?: string; email?: string } | null; error?: { code?: string; message?: string } | null }

const unwrap = (r: unknown): Record<string, unknown> | null => {
  const rec = r && typeof r === "object" && "data" in (r as Record<string, unknown>) ? (r as { data: unknown }).data : r;
  return rec && typeof rec === "object" ? rec as Record<string, unknown> : null;
};

/* Live per-capability status. Additive: if the op is unavailable the page keeps
 * rendering the backend's own feature status — never a fabricated state. */
function useMicrosoftStatus(provider: string, nonce: number): Record<string, MsCapability> | null {
  const [caps, setCaps] = useState<Record<string, MsCapability> | null>(null);
  useEffect(() => {
    if (provider !== MS_PROVIDER) { setCaps(null); return; }
    let alive = true;
    getMicrosoftStatus({ probe: false })
      .then((r) => { const rec = unwrap(r); if (alive) setCaps((rec?.capabilities as Record<string, MsCapability>) ?? null); })
      .catch(() => { if (alive) setCaps(null); });
    return () => { alive = false; };
  }, [provider, nonce]);
  return caps;
}

function CapabilityPill({ cap }: { cap: MsCapability }) {
  const t = useT();
  const s = String(cap.state ?? "");
  if (!s) return null;
  const map: Record<string, { tone: "success" | "warn" | "danger" | "neutral"; label: string }> = {
    connected: { tone: "success", label: t("settings.integrationDetail.capConnected", "Connected") },
    needs_consent: { tone: "warn", label: t("settings.integrationDetail.capNeedsConsent", "Needs admin consent") },
    error: { tone: "danger", label: t("settings.integrationDetail.capError", "Error") },
    not_configured: { tone: "neutral", label: t("settings.integrationDetail.capNotConfigured", "Not configured") },
    off: { tone: "neutral", label: t("settings.integrationDetail.capOff", "Off") },
  };
  const m = map[s] ?? { tone: "neutral" as const, label: s };
  return <Pill tone={m.tone} dot={m.tone === "success"}>{m.label}</Pill>;
}

/* One configuration field, rendered by declared type. */
export function ConfigField({ f, canWrite, provider, onSaved }: { f: IntegInput; canWrite: boolean; provider?: string; onSaved?: (d: RegDetail) => void }) {
  const t = useT();
  const [val, setVal] = useState<string>(typeof f.filled === "string" ? f.filled : "");
  const [on, setOn] = useState<boolean>(f.filled === true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const fieldLabel = t("settings.integrationDetail.fLFRequired.icu", { label: f.l, required: f.required ? "yes" : "no" });
  // Locked = no write access OR a backend-provisioned display-only field
  // (stripe-fe handoff §3: OAuth-provisioned values are never user-editable).
  const locked = !canWrite || !!f.readonly;
  // Payments work-order 2026-07-24 Task 2: every config input renders its
  // description below it (what it is / provisioned-automatically / constraints).
  const desc = f.desc ? <p style={{ fontSize: 11, color: T.soft, margin: "4px 0 0", lineHeight: 1.5 }}>{f.desc}</p> : null;
  if (f.type === "toggle") return (
    <div>
      <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", cursor: locked ? "default" : "pointer" }}>
        <Toggle on={on} onChange={locked ? undefined : () => setOn(!on)} />
        <span style={{ fontSize: 13, color: T.n1, fontWeight: 500 }}>{f.l}</span>
      </label>
      {desc ? <div style={{ paddingLeft: 42 }}>{desc}</div> : null}
    </div>
  );
  if (f.type === "date") {
    // Proper future-date selector for expiry-style fields (e.g. client secret
    // expiry) — native date picker, min = today, value normalized to YYYY-MM-DD.
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(val) ? val : (() => { const d = new Date(val); return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10); })();
    const today = new Date().toISOString().slice(0, 10);
    return (
      <div>
        <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{fieldLabel}</label>
        <input type="date" value={iso} min={today} disabled={locked}
          onChange={(e) => setVal(e.target.value)}
          style={{ width: "100%", height: 36, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.borderStrong}`, background: T.white, fontFamily: T.font, fontSize: 13, color: iso ? T.n1 : T.soft, boxSizing: "border-box" }} />
        {desc}
      </div>
    );
  }
  if (f.type === "select") return (
    <div>
      <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{fieldLabel}</label>
      <select value={val} onChange={(e) => setVal(e.target.value)} disabled={locked}
        style={{ width: "100%", height: 36, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.borderStrong}`, background: T.white, fontFamily: T.font, fontSize: 13, color: val ? T.n1 : T.soft }}>
        {!val ? <option value="">{f.placeholder || "Choose…"}</option> : null}
        {val ? <option value={val}>{val}</option> : null}
      </select>
      {desc}
    </div>
  );
  if (f.type === "mapping") return (
    <div>
      <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{fieldLabel}</label>
      {val ? (
        <div style={{ padding: "8px 12px", border: `1px solid ${T.border}`, borderRadius: 4, background: T.white, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: T.n1 }}>{val}</span>
          <Btn variant="ghost" size="sm" icon={<Icon.edit size={12} />}>{t("common.actions.edit", "Edit")}</Btn>
        </div>
      ) : (
        <div style={{ padding: 12, border: `1px dashed ${T.n4}`, borderRadius: 4, background: T.white, fontSize: 13, color: T.soft }}>
          {t("settings.integrationDetail.configureAfterConnecting", "Configure after connecting")}</div>
      )}
      {desc}
    </div>
  );
  // Item 1 (addendum, kit 9620814f): a value the backend reports as `configured`
  // renders READ-ONLY with a "Change" button — never edit-in-place over a mask.
  // Change clears to an empty editable field so a fresh value is typed; Save
  // PATCHes ONLY the re-typed field via updateIntegrationConfig, then re-reads
  // the registration (no-fake-flip). Backend-provisioned (readonly) fields stay
  // locked with no Change button. Legacy per-integration fields (no provider)
  // keep the prior editable behavior untouched.
  const canChange = !!provider && !!f.fieldKey && canWrite && !f.readonly;
  const save = async () => {
    if (!provider || !f.fieldKey || saving || !val.trim()) return;
    setSaving(true);
    try {
      await updateIntegrationConfig(provider, { config: { [f.fieldKey]: val } });
      const rec = regFromResponse(await getIntegrationDetail(provider));
      if (rec && onSaved) onSaved(rec);
      toast({ tone: "success", title: `${f.l} ${t("settings.integrationDetail.updated", "updated")}` });
      setEditing(false);
    } catch (e) { toast({ tone: "warn", title: `Couldn't update ${f.l}`, subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setSaving(false); }
  };
  if (f.configured && canChange && !editing) {
    return (
      <div>
        <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{f.l}</label>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0, height: 36, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.n6, display: "flex", alignItems: "center", gap: 8, fontFamily: f.type === "password" ? T.fontMono : T.font, fontSize: 13, color: T.n2 }}>
            {f.type === "password" ? <span style={{ color: T.soft, display: "inline-flex" }}><Icon.lock size={12} /></span> : null}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{typeof f.filled === "string" && f.filled ? f.filled : "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}</span>
          </div>
          <Btn variant="secondary" size="sm" onClick={() => { setVal(""); setEditing(true); }}>{t("settings.integrationDetail.change", "Change")}</Btn>
        </div>
        {desc}
      </div>
    );
  }
  return (
    <div>
      <Input
        label={fieldLabel}
        value={val}
        onChange={(e: any) => setVal(e.target.value)}
        placeholder={editing ? t("settings.integrationDetail.enterNewValue", "Enter a new value") : f.placeholder}
        // Stored secrets arrive pre-masked server-side (first chars + bullets, e.g.
        // "q7X8Q~kP••••") — render those as text so the identifying prefix shows.
        // Only mask live keystrokes on a fresh/replaced secret.
        type={f.type === "password" && !val.includes("•") ? "password" : "text"}
        leading={f.type === "password" ? <Icon.lock size={12} /> : undefined}
        readOnly={locked && !editing}
      />
      {editing ? (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <Btn size="sm" disabled={saving || !val.trim()} onClick={() => { void save(); }}>{saving ? t("settings.integrationDetail.saving", "Saving\u2026") : t("common.actions.save", "Save")}</Btn>
          <Btn variant="ghost" size="sm" disabled={saving} onClick={() => { setEditing(false); setVal(typeof f.filled === "string" ? f.filled : ""); }}>{t("common.actions.cancel", "Cancel")}</Btn>
        </div>
      ) : null}
      {desc}
    </div>
  );
}

/* Graph API logging add-on config (kit ebcdff34, PART B) — the one feature-key-
 * gated add-on panel: shown only when the backend exposes a graph-logging
 * feature on the registration and it is enabled. Settings persist via the
 * dedicated graph-log ops (awaited, honest errors). */
const GRAPH_LOG_FEATURE_KEYS = ["graph_logging", "msgraphlog"]; // ui-config — feature keys that expose the Graph-log settings panel

function GraphLogPanel({ canWrite }: { canWrite: boolean }) {
  const t = useT();
  type GLSettings = { enabled: boolean; retentionDays: number; logBodies: boolean };
  const [settings, setSettings] = useState<GLSettings | null>(null);
  const [settingsErr, setSettingsErr] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  useEffect(() => {
    let alive = true;
    getGraphLogSettings().then((r: any) => { if (alive) setSettings(r.data); }).catch((e) => { if (alive) setSettingsErr(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, []);
  const saveSettings = async (patch: Partial<GLSettings>) => {
    if (savingSettings || !settings) return;
    setSavingSettings(true);
    try {
      const r: any = await updateGraphLogSettings(patch);
      setSettings(r.data);
      toast({ tone: "success", title: t("settings.integrationDetail.graphLogSettingsSaved", "Logging settings saved") });
    } catch (e) { toast({ tone: "warn", title: t("settings.integrationDetail.graphLogSettingsSaveFailed", "Couldn't save logging settings"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setSavingSettings(false); }
  };
  return (
    <Card style={{ padding: 0 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
        <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.integrationDetail.graphApiLogging", "Graph API logging")}</h3>
        <p style={{ ...tx.help, marginTop: 2 }}>{t("settings.integrationDetail.graphApiLoggingSub", "Audit log of every Microsoft Graph call this registration makes.")}</p>
      </div>
      {/* Settings — one toggle (operator 2026-07-22): encrypted body capture. The
       * kit's enabled/retention settings stay backend-side; the feature row's own
       * toggle already covers on/off. */}
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
        {settingsErr ? <span style={{ fontSize: 12, color: T.red1 }}>{settingsErr}</span> : !settings ? <span style={{ fontSize: 12, color: T.soft }}>{t("settings.integrationDetail.loadingSettings", "Loading settings…")}</span> : (
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: canWrite ? "pointer" : "default" }}>
            <span style={{ marginTop: 2, opacity: savingSettings ? 0.5 : 1, display: "inline-flex" }}>
              <Toggle on={settings.logBodies} onChange={canWrite && !savingSettings ? () => { void saveSettings({ logBodies: !settings.logBodies }); } : undefined} />
            </span>
            <span>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, display: "block" }}>{t("settings.integrationDetail.captureBodies", "Capture request & response bodies")}</span>
              <span style={{ fontSize: 12, color: T.soft, lineHeight: 1.5 }}>{t("settings.integrationDetail.captureBodiesSub", "May contain personal data (PII) — stored AES-256 encrypted, decrypted only when you open a log row.")}</span>
            </span>
          </label>
        )}
      </div>
      {/* Log viewer intentionally NOT rendered here (operator 2026-07-22): the
       * integration page carries only the SETTING. The full log table/stats/CSV
       * live behind the graph-logs ops for a dedicated observability surface. */}
    </Card>
  );
}

/* ─── Left column: shared credentials + "Features on this registration" ── */
export function RegistrationPanels({ detail, focusKey, canWrite, onDetail }: { detail: RegDetail; focusKey?: string | null; canWrite: boolean; onDetail: (d: RegDetail) => void }) {
  const t = useT();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [graphLogOpen, setGraphLogOpen] = useState(false);
  // Microsoft layer (restored 2026-07-27): live capability status + verify.
  const isMs = detail.provider === MS_PROVIDER;
  const [statusNonce, setStatusNonce] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [verifyRes, setVerifyRes] = useState<MsVerifyResult | null>(null);
  const msCaps = useMicrosoftStatus(detail.provider, statusNonce);
  const feats = regFeatures(detail);
  const fields = Array.isArray(detail.configFields) ? detail.configFields : [];
  // Item 3 (addendum): without shared credentials a feature can't be switched
  // on — gate every toggle and show a hint until creds are saved/connected.
  const requiresInput = regRequiresInput(detail);
  // Remount config fields whenever the backend record changes (e.g. after a
  // disconnect cascade clears values) so their local input state re-seeds from
  // the fresh configFields instead of holding stale text/masks.
  const recordVer = `${detail.updatedAt ?? ""}:${detail.status ?? ""}:${requiresInput}`;
  const graphLogFeature = feats.find((f) => GRAPH_LOG_FEATURE_KEYS.includes(f.key));
  // Pre-focus (grid deep-link): tint the tapped service's row and bring it into
  // view by adjusting the nearest scrollable ancestor (never scrollIntoView).
  const focusRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    const el = focusRef.current;
    if (!el) return;
    let p: HTMLElement | null = el.parentElement;
    while (p && p.scrollHeight <= p.clientHeight + 1) p = p.parentElement;
    if (p) p.scrollTop += el.getBoundingClientRect().top - p.getBoundingClientRect().top - 96;
  }, [focusKey, detail.provider]);
  // No-fake-interactivity: the toggle flips only from the awaited PATCH's
  // response (the recomputed IntegrationDetail); failures change nothing.
  const doToggle = async (f: RegFeature) => {
    if (busyKey || !canWrite) return;
    const next = !(f.requestedEnabled ?? f.enabled);
    setBusyKey(f.key);
    try {
      const r = await updateIntegrationFeature(detail.provider, f.key, { enabled: next });
      const rec = regFromResponse(r);
      if (rec) onDetail(rec);
      void refreshConnections(); // member catalog rows (grid pills) re-read the server
      setStatusNonce((n) => n + 1); // capability pills re-read after every toggle
      toast({ tone: "success", title: `${f.name} ${next ? t("settings.integrationDetail.featureEnabled", "enabled") : t("settings.integrationDetail.featureDisabled", "disabled")}` });
    } catch (e) {
      toast({ tone: "warn", title: `Couldn't ${next ? "enable" : "disable"} ${f.name}`, subtitle: e instanceof Error ? e.message : String(e) });
    } finally { setBusyKey(null); }
  };
  /* Verify connection (MS Part C, restored). Result is read from the payload's
   * `ok` / `error.code` — never the HTTP status; a transport-level rejection
   * renders as PROVIDER_UNREACHABLE, never a fake success. */
  const doVerify = async () => {
    if (verifying) return;
    setVerifying(true);
    setVerifyRes(null);
    try {
      const rec = unwrap(await verifyMicrosoftConnection()) as MsVerifyResult | null;
      setVerifyRes(rec ?? { ok: false, error: { code: "PROVIDER_UNREACHABLE" } });
    } catch (e) {
      setVerifyRes({ ok: false, error: { code: "PROVIDER_UNREACHABLE", message: e instanceof Error ? e.message : String(e) } });
    } finally {
      setVerifying(false);
      setStatusNonce((n) => n + 1); // capability pills re-read after every verify
    }
  };
  /* Grouped features (2026-08-04): a registration that declares API PRODUCTS
   * (Twilio: Programmable Messaging vs Conversations) renders one titled section per
   * product — the product's own service settings, then its channels. A registration
   * with no groups (Google, Microsoft) renders the same flat list as before. */
  const groupDefs: RegFeatureGroup[] = Array.isArray(detail.featureGroups) ? detail.featureGroups : [];
  const featureRows: { group?: RegFeatureGroup; feature?: RegFeature }[] = [];
  if (groupDefs.length) {
    const claimed: Record<string, boolean> = {};
    groupDefs.forEach((g) => {
      const members = feats.filter((f) => f.group === g.key);
      if (!members.length && !(Array.isArray(g.configFields) ? g.configFields.length : 0)) return;
      members.forEach((f) => { claimed[f.key] = true; });
      featureRows.push({ group: g });
      members.forEach((f) => featureRows.push({ feature: f }));
    });
    feats.filter((f) => !claimed[f.key]).forEach((f) => featureRows.push({ feature: f }));
  } else {
    feats.forEach((f) => featureRows.push({ feature: f }));
  }
  return (
    <>
      <Card style={{ padding: 0 }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12 }}>
          <ProviderLogo id={detail.provider} name={detail.name} brand={detail.brand || T.n1} glyph={detail.icon} size={32} radius={8} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{detail.name}</h3>
            <p style={{ ...tx.help, marginTop: 2 }}>{detail.desc ?? ""}</p>
          </div>
          {/* Only where something IS shared at registration level. A per-company
              certificate signing per feature (Apple Pass) is not shared — saying so
              two lines under "nothing is shared across tenants" was the operator's
              compliance flag. */}
          {(detail.configFields?.length ?? 0) > 0
            ? <Pill tone="info">{t("settings.integrationDetail.sharedAcrossFeatures", "Shared across features")}</Pill>
            : null}
          {isMs ? (
            <Btn variant="secondary" size="sm" icon={<Icon.refresh size={12} />} disabled={verifying} onClick={() => { void doVerify(); }}>
              {verifying ? t("settings.integrationDetail.verifying", "Verifying…") : t("settings.integrationDetail.verifyConnection", "Verify connection")}
            </Btn>
          ) : null}
        </div>
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Verify result banner — success shows the account it authenticated as;
            * failure maps the backend error CODE to actionable copy. */}
          {verifyRes ? (verifyRes.ok ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center", padding: 12, borderRadius: 6, background: T.green2, border: `1px solid color-mix(in srgb, ${T.green1} 19%, transparent)` }}>
              <span style={{ width: 24, height: 24, borderRadius: 999, background: T.green1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.check size={12} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("settings.integrationDetail.connectionVerified", "Connection verified")}</div>
                <div style={{ fontSize: 12, color: T.n2 }}>
                  {[verifyRes.account?.label, verifyRes.account?.email].filter(Boolean).join(" · ")}
                  {typeof verifyRes.latency_ms === "number" ? ` · ${verifyRes.latency_ms} ms` : ""}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: 12, borderRadius: 6, background: `color-mix(in srgb, ${T.red1} 7%, ${T.white})`, border: `1px solid color-mix(in srgb, ${T.red1} 25%, transparent)` }}>
              <span style={{ width: 24, height: 24, borderRadius: 999, background: T.red1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.warn size={12} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("settings.integrationDetail.verifyFailed", "Verification failed")}</div>
                <div style={{ fontSize: 12, color: T.n2, lineHeight: 1.5 }}>
                  {(() => {
                    const m = MS_VERIFY_ERRORS[String(verifyRes.error?.code ?? "")];
                    return m ? t(m.k, m.l) : (verifyRes.error?.message || t("settings.integrationDetail.verifyUnknownError", "Unknown error"));
                  })()}
                </div>
              </div>
            </div>
          )) : null}
          {fields.map((f, i) => <ConfigField key={`${recordVer}-${i}`} f={toInput(f)} canWrite={canWrite} provider={detail.provider} onSaved={onDetail} />)}
          {/* Item 1 (addendum #2): the OAuth redirect URI flows OUT to the provider
           * console — render it read-only with Copy, visible even when disconnected. */}
          {detail.callback ? (
            <div>
              <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{detail.callbackLabel || t("settings.integrationDetail.redirectUri", "Redirect URI")}</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <code style={{ flex: 1, minWidth: 0, height: 36, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.n6, display: "flex", alignItems: "center", fontFamily: T.fontMono, fontSize: 12, color: T.p1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{detail.callback}</code>
                <Btn variant="secondary" size="sm" icon={<Icon.copy size={12} />} onClick={() => { try { navigator.clipboard.writeText(String(detail.callback)); } catch { /* ignore */ } toast({ tone: "success", title: t("settings.integrationDetail.copied", "Copied") }); }}>{t("settings.integrationDetail.copy", "Copy")}</Btn>
              </div>
              <p style={{ fontSize: 11, color: T.soft, margin: "4px 0 0", lineHeight: 1.5 }}>{detail.callbackHint || t("settings.integrationDetail.redirectUriHint", "Add this redirect URI to your provider console (Google Cloud / Azure) before connecting.")}</p>
            </div>
          ) : null}
          {detail.docs ? <p style={{ ...tx.help, margin: 0 }}>{detail.docs}</p> : null}
        </div>
      </Card>
      <Card style={{ padding: 0 }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
          <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{detail.featuresTitle || t("settings.integrationDetail.featuresOnThisRegistration", "Features on this registration")}</h3>
          <p style={{ ...tx.help, marginTop: 2 }}>{detail.featuresSubtitle || t("settings.integrationDetail.eachFeatureIsSwitchedIndividually", "Credentials are shared — each feature is switched on or off individually.")}</p>
          {requiresInput ? <p style={{ ...tx.help, marginTop: 4, color: T.orange1 }}>{t("settings.integrationDetail.enterCredentialsToEnable", "Enter credentials to enable these features.")}</p> : null}
        </div>
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {featureRows.map((row, i) => {
            if (row.group) {
              const g = row.group;
              const gFields = Array.isArray(g.configFields) ? g.configFields : [];
              return (
                <li key={`group-${g.key}`} style={{ padding: "12px 16px", borderTop: i ? `1px solid ${T.border}` : "none", background: T.n6, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{g.name ?? g.key}</div>
                    {g.description ? <p style={{ ...tx.help, marginTop: 2 }}>{g.description}</p> : null}
                  </div>
                  {gFields.length ? (
                    <div style={{ display: "grid", gap: 10, maxWidth: 520 }}>
                      {gFields.map((cf, ci) => <ConfigField key={`${recordVer}-${g.key}-${ci}`} f={toInput(cf)} canWrite={canWrite} provider={detail.provider} onSaved={onDetail} />)}
                    </div>
                  ) : null}
                </li>
              );
            }
            const f = row.feature as RegFeature;
            const focused = focusKey != null && f.key === focusKey;
            const cap = msCaps ? msCaps[MS_FEATURE_CAPABILITY[f.key] ?? ""] : undefined;
            // Add-ons (graph logging) have no Microsoft capability — the old MS page
            // showed no pill for them rather than the raw record status string.
            const hidePill = !cap && !!msCaps && MS_FEATURE_CAPABILITY[f.key] === undefined;
            return (
              <li key={f.key} ref={focused ? focusRef : undefined} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 16px", borderTop: i ? `1px solid ${T.border}` : "none", background: focused ? `color-mix(in srgb, ${T.p1} 4%, ${T.white})` : T.white }}>
                <ProviderLogo id={`${detail.provider}_${f.key}`} name={f.name} brand={detail.brand || T.n1} glyph={f.icon || detail.icon} size={28} radius={6} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{f.name}</span>
                    {/* Live capability state wins over the record's own status string. */}
                    {cap ? <CapabilityPill cap={cap} /> : hidePill ? null : <FeatureStatusPill status={f.status} label={f.statusLabel} />}
                  </div>
                  <div style={{ fontSize: 12, color: T.soft, marginTop: 2, lineHeight: 1.5 }}>{f.description ?? ""}</div>
                  {cap?.state === "needs_consent" ? (
                    <div style={{ fontSize: 11, color: T.orange1, marginTop: 4, lineHeight: 1.5 }}>{t("settings.integrationDetail.grantAdminConsentHint", "Grant admin consent in the Microsoft Entra admin center to activate this permission.")}</div>
                  ) : null}
                  {cap?.state === "error" && cap?.error?.message ? (
                    <div style={{ fontSize: 11, color: T.red1, marginTop: 4, lineHeight: 1.5 }}>{cap.error.message}</div>
                  ) : null}
                  {/* Mail.Send consent caveat: rendered even when the state is connected. */}
                  {cap?.note ? (
                    <div style={{ fontSize: 11, color: T.soft, fontStyle: "italic", marginTop: 4, lineHeight: 1.5 }}>{cap.note}</div>
                  ) : null}
                  {f.adminConsentRequired ? (
                    <div style={{ fontSize: 11, color: T.orange1, marginTop: 4, lineHeight: 1.5 }}>{t("settings.integrationDetail.adminConsentRequired", "Admin consent required")}</div>
                  ) : null}
                  {Array.isArray(f.scopes) && f.scopes.length ? (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                      {f.scopes.map((s) => <code key={s} style={{ fontFamily: T.fontMono, fontSize: 10, color: T.soft, background: T.n6, padding: "2px 6px", borderRadius: 3 }}>{s}</code>)}
                    </div>
                  ) : null}
                  {/* SSO-central note (addendum #3): staff SSO is governed centrally,
                   * not per-tenant — never render a tenant SSO config form. */}
                  {f.ssoManagedExternally ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 11, color: T.n2 }}>
                      <span style={{ color: T.blue1, display: "inline-flex" }}><Icon.info size={11} /></span>
                      <span>{t("settings.integrationDetail.ssoManagedCentrally", "Staff single sign-on is managed centrally by your platform administrator.")}</span>
                    </div>
                  ) : null}
                  <span style={{ flexShrink: 0, marginTop: 2, display: "none" }} />
                  {f.requiredApi ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 11, color: T.n2 }}>
                      <span style={{ color: T.soft, display: "inline-flex" }}><Icon.info size={11} /></span>
                      <span>{t("settings.integrationDetail.enableInProviderConsole", "Enable in the provider console:")}{" "}<strong style={{ color: T.n1, fontWeight: 600 }}>{f.requiredApi}</strong></span>
                    </div>
                  ) : null}
                  {/* A channel's own settings (its sender) live on the feature, not on the
                    * shared-credential card — shown once it is switched on, saved through the
                    * same PATCH /config surface. Fields carrying an `upload` block are
                    * FILE credentials (Apple Wallet cert + pass images) and render as the
                    * upload group; the certificate password rides that group too. */}
                  {/* Gated on the feature being REQUESTED on, not EFFECTIVE. The backend reports `enabled` as
                    * (decision AND required credentials present), so gating a credential-UPLOAD form on it
                    * deadlocks: the form is the only way to supply the certificate that makes `enabled` true.
                    * `requestedEnabled` is the stored decision; `status === "requires_input"` means exactly
                    * "switched on, credentials missing" — the state in which the form MUST render. */}
                  {(f.requestedEnabled === true || f.enabled || f.status === "requires_input")
                    && Array.isArray(f.configFields) && f.configFields.length ? (
                    f.configFields.some((cf) => !!(cf as Record<string, unknown>).upload) ? (
                      <div style={{ display: "grid", gap: 10, marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${T.border}`, maxWidth: 520 }}>
                        <FeatureCredentialUploads provider={String(detail.provider)} featureKey={f.key}
                          fields={f.configFields} canWrite={canWrite}
                          onSaved={() => { void (async () => {
                            const rec = regFromResponse(await getIntegrationDetail(detail.provider));
                            if (rec) onDetail(rec);
                          })(); }} />
                        {/* JAI-581: the three tenant-default pass colours are pulled OUT of the
                          * generic text-field map and rendered as the Appearance group below —
                          * they are one decision with one Save, one shared contrast advisory, and
                          * a null "inherit the platform default" state a text input cannot express.
                          * pass_background_image is NOT here: it is an image-asset and rides the
                          * upload group above with the icon and logo slots. */}
                        {f.configFields.filter((cf) => !(cf as Record<string, unknown>).upload
                            && (PASS_COLOR_KEYS as readonly string[]).indexOf(String((cf as Record<string, unknown>).key ?? "")) < 0
                            && !(String((cf as Record<string, unknown>).type ?? "") === "password" && /password/i.test(String((cf as Record<string, unknown>).key ?? ""))))
                          .map((cf, ci) => <ConfigField key={`${recordVer}-${f.key}-x-${ci}`} f={toInput(cf)} canWrite={canWrite} provider={detail.provider} onSaved={onDetail} />)}
                        {f.configFields.some((cf) => (PASS_COLOR_KEYS as readonly string[]).indexOf(String((cf as Record<string, unknown>).key ?? "")) >= 0) ? (
                          <WalletAppearanceGroup key={`${f.key}-appearance`} provider={String(detail.provider)} feature={f} canWrite={canWrite} onSaved={onDetail} />
                        ) : null}
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 10, marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${T.border}`, maxWidth: 520 }}>
                        {f.configFields.map((cf, ci) => <ConfigField key={`${recordVer}-${f.key}-${ci}`} f={toInput(cf)} canWrite={canWrite} provider={detail.provider} onSaved={onDetail} />)}
                      </div>
                    )
                  ) : null}
                </div>
                <span style={{ flexShrink: 0, marginTop: 2, display: "flex", alignItems: "center", gap: 8 }}>
                  {graphLogFeature && f.key === graphLogFeature.key && f.enabled ? (
                    <Btn variant="secondary" size="sm" icon={<Icon.settings size={16} />} onClick={() => setGraphLogOpen((v) => !v)}>{graphLogOpen ? t("settings.integrationDetail.hideConfiguration", "Hide") : t("settings.integrationDetail.configure", "Configure")}</Btn>
                  ) : null}
                  <span style={{ opacity: busyKey === f.key ? 0.5 : 1, display: "inline-flex" }}>
                    <Toggle on={f.requestedEnabled ?? f.enabled} onChange={canWrite && busyKey == null && !requiresInput ? () => { void doToggle(f); } : undefined} />
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </Card>
      {graphLogOpen && graphLogFeature?.enabled ? <GraphLogPanel canWrite={canWrite} /> : null}
      {/* Directory-sync panel (addendum #3): Google Workspace only — the gsuite/manage
       * ops are Google-specific; shows when the `directory` feature is enabled. */}
      {detail.provider === "google" && feats.some((f) => f.key === "directory" && f.enabled) ? <GsuiteDirectoryPanel canWrite={canWrite} /> : null}
    </>
  );
}

/* ─── Right column: aggregated scopes across the ENABLED features ───────── */
export function RegistrationScopes({ detail }: { detail: RegDetail }) {
  const t = useT();
  // Every feature contributes its scopes; the ones only a DISABLED feature asks
  // for render dimmed as "not requested" rather than disappearing, so the admin
  // sees the full consent surface a feature would need before switching it on.
  type ScopeRow = { scope: string; on: string[]; off: string[]; adminConsent: boolean };
  const rows: ScopeRow[] = [];
  regFeatures(detail).forEach((f) => (f.scopes ?? []).forEach((s) => {
    let row = rows.find((x) => x.scope === s);
    if (!row) { row = { scope: s, on: [], off: [], adminConsent: false }; rows.push(row); }
    (f.enabled ? row.on : row.off).push(f.name);
    if (f.enabled && f.adminConsentRequired) row.adminConsent = true;
  }));
  return (
    <Card style={{ padding: 20 }}>
      <div style={tx.label as CSSProperties}>{detail.scopesTitle || t("settings.integrationDetail.permissionsScopes", "Permissions / scopes")}</div>
      <p style={{ ...tx.help, marginTop: 4 }}>{detail.scopesHint ? detail.scopesHint : detail.provider === MS_PROVIDER
        ? t("settings.integrationDetail.scopesFollowEnabledFeatures", "Requested from Microsoft based on which features are enabled — switching a feature off drops its scopes from the consent.")
        : t("settings.integrationDetail.grantedByEnabledFeatures", "Requested by the features enabled on this registration.")}</p>
      {rows.length === 0 ? (
        <div style={{ marginTop: 12, padding: 12, border: `1px dashed ${T.n4}`, borderRadius: 6, fontSize: 12, color: T.soft }}>
          {t("settings.integrationDetail.noFeaturesEnabledYet", "No features enabled yet — switch one on to see the permissions it requires.")}</div>
      ) : (
        <ul style={{ listStyle: "none", margin: "12px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
          {rows.map(({ scope, on, off, adminConsent }) => {
            const meta = scopeMeta(scope);
            const active = on.length > 0;
            return (
              <li key={scope} style={{ display: "flex", flexDirection: "column", gap: 4, opacity: active ? 1 : 0.55 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ color: active ? T.green1 : T.soft, display: "inline-flex" }}><Icon.check size={12} /></span>
                  <code style={{ fontFamily: T.fontMono, fontSize: 11, fontWeight: 600, color: T.n1 }}>{scope}</code>
                  {meta ? <Pill tone="neutral">{meta.delegated ? t("settings.integrationDetail.permissionTypeDelegated", "Delegated") : t("settings.integrationDetail.permissionTypeApplication", "Application · admin approval")}</Pill> : null}
                  {adminConsent ? <Pill tone="warn">{t("settings.integrationDetail.adminConsentRequired", "Admin consent required")}</Pill> : null}
                  {meta?.optional ? <Pill tone="neutral">{t("settings.integrationDetail.scopeOptional", "Optional")}</Pill> : null}
                </div>
                {meta?.note ? <div style={{ fontSize: 11, color: T.n2, lineHeight: 1.5, paddingLeft: 20 }}>{meta.note}</div> : null}
                <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.5, paddingLeft: 20 }}>
                  {active ? on.join(", ") : `${off.join(", ")} — ${t("settings.integrationDetail.notRequestedFeatureOff", "not requested (feature off)")}`}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/* ─── Right column: recent activity from the registration record ────────── */
type ActRow = { e: string; at: string };
const actRows = (src: RegDetail | null | undefined): ActRow[] =>
  (Array.isArray(src?.activity) ? (src as RegDetail).activity ?? [] : [])
    .map((a) => ({ e: String(a.e ?? a.detail ?? ""), at: String(a.at ?? a.occurredAt ?? "") }))
    .filter((a) => a.e);

export function RegistrationActivity({ detail }: { detail: RegDetail }) {
  const t = useT();
  const isMs = detail.provider === MS_PROVIDER;
  // Categorized activity (operator 2026-07-22, restored 2026-07-27): each
  // Microsoft feature's rows are read from its OWN backend record and grouped
  // under a category title — nothing invented UI-side.
  const [groups, setGroups] = useState<{ key: string; title: string; rows: ActRow[] }[] | null>(null);
  const [loadingGroups, setLoadingGroups] = useState(isMs);
  useEffect(() => {
    if (!isMs) { setGroups(null); setLoadingGroups(false); return; }
    let alive = true;
    setLoadingGroups(true);
    void (async () => {
      const out: { key: string; title: string; rows: ActRow[] }[] = [];
      for (const cat of MS_ACTIVITY_CATEGORIES) {
        const rows: ActRow[] = [];
        for (const member of cat.members) {
          const rec = regFromResponse(await getIntegrationDetail(member).catch(() => null));
          rows.push(...actRows(rec));
        }
        if (rows.length) out.push({ key: cat.key, title: cat.title, rows });
      }
      if (alive) { setGroups(out); setLoadingGroups(false); }
    })();
    return () => { alive = false; };
  }, [isMs, detail.provider, detail.updatedAt, detail.status]);
  const flat = actRows(detail);
  const empty = isMs ? !loadingGroups && (groups?.length ?? 0) === 0 : flat.length === 0;
  return (
    <Card style={{ padding: 0 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
        <div style={tx.label as CSSProperties}>{t("settings.integrationDetail.recentActivity", "Recent activity")}</div>
      </div>
      {isMs && loadingGroups ? (
        <div style={{ padding: 20, fontSize: 12, color: T.soft, textAlign: "center" }}>{t("settings.integrationDetail.loadingActivity", "Loading activity…")}</div>
      ) : empty ? (
        <div style={{ padding: 20, fontSize: 12, color: T.soft, textAlign: "center" }}>
          {t("settings.integrationDetail.noActivityYetConnectToStartLoggingEvents", "No activity yet — connect to start logging events.")}</div>
      ) : isMs && groups ? (
        <div>
          {groups.map((g, gi) => (
            <section key={g.key} style={{ borderTop: gi ? `1px solid ${T.border}` : "none" }}>
              <div style={{ padding: "10px 16px 4px", fontSize: 11, fontWeight: 700, color: T.n1, letterSpacing: 0.2 }}>{t(`settings.integrationDetail.activityCat.${g.key}`, g.title)}</div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {g.rows.map((a, i) => (
                  <li key={`${g.key}-${i}`} style={{ padding: "8px 16px", display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.e}</span>
                    <span style={{ fontSize: 11, color: T.soft, flexShrink: 0 }}>{a.at}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {flat.map((a, i) => (
            <li key={i} style={{ padding: "10px 16px", borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 12, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.e}</span>
              <span style={{ fontSize: 11, color: T.soft, flexShrink: 0 }}>{a.at}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}


/* ───────────────────────────────────────────────────────────────────────────
 * WalletAppearanceGroup (JAI-581) — tenant-default pass colours.
 *
 * Composes jrni-ui's ColorPicker rather than hand-rolling a hex input: a third
 * hex field sitting next to the primitive it duplicates is the fork the design
 * system exists to prevent. Two things the primitive does not carry, both held
 * OUTSIDE it rather than routed around it, and both worth raising against the
 * library since the next colour surface hits the same wall:
 *   1. No null state. `value` is a required string, so "unset / inherit the
 *      default" has no representation. The Use default control below owns that,
 *      and the picker is only mounted once a colour exists.
 *   2. Its hex input accepts 3-digit shorthand and normalises it. Harmless
 *      here — it always EMITS #RRGGBB, which is all the backend sees — but it
 *      means the tolerant reading lives in the input affordance, not the wire.
 * Its built-in `contrastAgainst` indicator IS wired (2026-08-20), one pair per
 * open picker, against the counterpart that colour is read against on the pass:
 * text and label against the effective background, the background against the
 * resolved label colour. That is live per-keystroke feedback while choosing. It
 * does not replace the aggregated notice below — that one is measured across all
 * three pass styles and lands after the fact, which is a different question.
 *
 * Open/closed state is held here too, not in the primitive: it has no `open` /
 * `onClose`, correctly, because Journey Builder branding mounts it in an
 * always-visible panel. Behind a collapsible row the mounting screen owes the
 * dismissal — see `openKey`.
 * ─────────────────────────────────────────────────────────────────────────── */

const PASS_COLOR_LABELS: Record<PassColorKey, string> = {
  pass_background_color: "Pass background colour",
  pass_foreground_color: "Pass text colour",
  pass_label_color: "Pass label colour",
};
const PASS_STYLE_NAMES: Record<string, string> = { eventTicket: "Event ticket", appointment: "Appointment", queue: "Queue position" };

export function WalletAppearanceGroup({ provider, feature, canWrite, onSaved }: {
  provider: string; feature: RegFeature; canWrite: boolean; onSaved: (d: RegDetail) => void;
}) {
  /* State model: we hold ONLY the keys the operator has actually edited, and
   * derive the effective value per key as "edited if edited, else stored".
   *
   * The obvious model — copy `stored` into a draft and re-seed it with an
   * effect when the record changes — is wrong here, and was the defect: `stored`
   * is recomputed from `feature.configFields` on every render, and the record
   * legitimately changes underneath this component (a sibling field saving, a
   * feature toggle, the post-save re-read). Any of those re-seeds fired
   * mid-edit and silently discarded the colour the operator had just committed,
   * leaving the picker displaying one value while the draft held another —
   * Save then persisted the stale one, or went inert because `dirty` computed
   * false against a draft that had been reset to match `stored`.
   *
   * With edits held separately there is nothing to clobber: a record change
   * updates the baseline, edits survive it, and `dirty` is simply "are there
   * any edits that differ from the baseline". */
  const stored = readPassAppearance(feature.configFields);
  const [edits, setEdits] = useState<Partial<PassAppearance>>({});
  const [saving, setSaving] = useState(false);
  /* Which field's picker is EXPANDED. Open/closed state belongs HERE, not in
   * ColorPicker: the primitive is a presentational inline panel with no `open` /
   * `onClose` in its prop surface (correct for Journey Builder branding, which
   * mounts it in an always-visible panel), so the screen that mounts it behind
   * "Set a colour" owns dismissing it. Open-ness used to be IMPLIED by "a colour
   * exists", which left the panel expanded forever with only the destructive Use
   * default underneath. Done collapses and KEEPS the choice. Deliberately not
   * auto-collapsing on change: dragging the saturation area and typing a hex both
   * emit onChange continuously, so collapse-on-change would close mid-drag. */
  const [openKey, setOpenKey] = useState<PassColorKey | null>(null);
  /* Mirror of `edits` that Save reads instead of its render closure. The hex
   * field commits on blur, and that blur is usually the same interaction that
   * presses Save — clicking the button blurs the field. Commit and click are
   * separate events, so a closure captured before the commit still held the
   * previous colour and Save persisted THAT while the field showed what the
   * operator had just typed. The ref is written synchronously inside `set`, so
   * it is correct the instant ColorPicker's onChange returns. */
  const editsRef = useRef<Partial<PassAppearance>>({});

  const valueOf = (k: PassColorKey): string | null => (k in edits ? (edits[k] ?? null) : stored[k]);
  const draft: PassAppearance = {
    pass_background_color: valueOf("pass_background_color"),
    pass_foreground_color: valueOf("pass_foreground_color"),
    pass_label_color: valueOf("pass_label_color"),
  };
  const changed = PASS_COLOR_KEYS.filter((k) => k in edits && draft[k] !== stored[k]);
  const dirty = changed.length > 0;
  const notice = passContrastNotice(draft);
  const eventTicket = PASS_STYLE_DEFAULTS.find((d) => d.issued) ?? PASS_STYLE_DEFAULTS[0];

  const set = (k: PassColorKey, v: string | null) => {
    const next = { ...editsRef.current, [k]: v };
    editsRef.current = next;
    setEdits({ ...next });
  };

  const save = async () => {
    if (saving || !canWrite) return;
    /* Read the edits from the REF, never from this render's closure. The hex
     * field commits on blur, and the blur that commits it is usually the same
     * interaction that presses Save — clicking the button blurs the field. The
     * commit and the click are separate events, so a closure captured before the
     * commit still held the previous colour: Save then persisted THAT while the
     * field on screen showed what the operator had just typed. Writing a value
     * the operator can see is not the one they entered is the worst failure this
     * control has. */
    const live = editsRef.current;
    const liveDraft: PassAppearance = {
      pass_background_color: "pass_background_color" in live ? (live.pass_background_color ?? null) : stored.pass_background_color,
      pass_foreground_color: "pass_foreground_color" in live ? (live.pass_foreground_color ?? null) : stored.pass_foreground_color,
      pass_label_color: "pass_label_color" in live ? (live.pass_label_color ?? null) : stored.pass_label_color,
    };
    /* Strict #RRGGBB validated before sending: the backend rejects anything else
     * rather than normalising it, so nothing mid-keystroke or partial reaches the
     * wire. Clearing sends an explicit null, never "" — the backend keeps the two
     * distinguishable ("inherit the default" vs "blank this") and "" fails
     * validation. Clear-to-empty is local state precisely so the invalid request
     * cannot be constructed. */
    const writes: { key: PassColorKey; value: string | null }[] = [];
    for (const k of PASS_COLOR_KEYS) {
      if (!(k in live)) continue;
      const v = liveDraft[k];
      if (v === stored[k]) continue;
      if (v !== null && !isPassHex(v)) {
        toast({ tone: "warn", title: `${PASS_COLOR_LABELS[k]} isn't a six-digit hex colour`, subtitle: "Use #RRGGBB — the server accepts nothing else." });
        return;
      }
      writes.push({ key: k, value: v === null ? null : v.toUpperCase() });
    }
    if (!writes.length) {
      // The button is deliberately never disabled (see its comment), so a click
      // with nothing pending has to answer rather than look broken.
      toast({ tone: "info", title: "No appearance changes to save" });
      return;
    }
    setSaving(true);
    try {
      /* ONE PUT PER FIELD — the feature-settings route has no batch form, so a
       * three-colour save is three requests and the set is NOT atomic. They run
       * sequentially rather than in parallel so a failure stops at a known point:
       * everything before it is committed, everything after it untouched. The
       * re-read below then shows exactly what landed, so a partial failure
       * reports honestly instead of leaving the screen claiming a whole set was
       * saved. `updateIntegrationConfig` is NOT usable here — it 400s
       * CONFIG_NOT_SUPPORTED for apple and writes a store with no concept of
       * features (platform-confirmed 2026-08-13). */
      let done = 0;
      try {
        for (const w of writes) {
          await putIntegrationFeatureSetting(provider, feature.key, w.key, w.value);
          done += 1;
        }
      } catch (e) {
        const rec = regFromResponse(await getIntegrationDetail(provider).catch(() => null));
        if (rec) onSaved(rec);
        // Keep the edits: the operator's unsaved colours are still theirs to retry.
        toast({
          tone: "warn",
          title: done ? `Saved ${done} of ${writes.length} colours` : "Couldn't update pass appearance",
          subtitle: e instanceof Error ? e.message : String(e),
        });
        return;
      }
      const rec = regFromResponse(await getIntegrationDetail(provider));
      if (rec) onSaved(rec);
      // Cleared only after the re-read lands, so the group falls back to the
      // freshly-stored values rather than flashing the old baseline.
      editsRef.current = {};
      setEdits({});
      toast({ tone: "success", title: "Pass appearance updated" });
    } catch (e) {
      toast({ tone: "warn", title: "Couldn't update pass appearance", subtitle: e instanceof Error ? e.message : String(e) });
    } finally { setSaving(false); }
  };

  return (
    <div style={{ display: "grid", gap: 12, marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${T.border}` }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>Appearance</div>
        <p style={{ fontSize: 11, color: T.soft, margin: "4px 0 0", lineHeight: 1.5 }}>
          One colour set applies to every pass style. Anything left on the default inherits the platform colour, which differs per style.
        </p>
      </div>

      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
        {PASS_COLOR_KEYS.map((k) => {
          const v = draft[k];
          const fallback = k === "pass_background_color" ? eventTicket.bg : k === "pass_foreground_color" ? eventTicket.fg : resolvePassColors(eventTicket, draft).label;
          const open = openKey === k && v !== null;
          /* The picker's own contrast indicator, wired to the pair the colour is
           * actually read against on the pass: text and label sit ON the
           * background, and the background is judged against the resolved label
           * colour. Live per-keystroke feedback while choosing. The amber banner
           * below is untouched - backend guidance, advisory by design. */
          const against = k === "pass_background_color"
            ? resolvePassColors(eventTicket, draft).label
            : (draft.pass_background_color ?? eventTicket.bg);
          return (
            <div key={k} style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
              {open ? (
                <>
                  <ColorPicker value={v as string} onChange={(hex) => set(k, hex)} label={PASS_COLOR_LABELS[k]} disabled={!canWrite} contrastAgainst={against} style={{ width: "100%" }} />
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {/* Keep-and-collapse. The chosen colour stays in the draft and the
                      * row below shows it as a swatch + hex, matching how a field that
                      * was already set renders. Saving is still the group's one Save. */}
                    <Btn variant="secondary" size="sm" disabled={!canWrite} onClick={() => setOpenKey(null)}>Done</Btn>
                    <Btn variant="ghost" size="sm" disabled={!canWrite} onClick={() => { set(k, null); setOpenKey(null); }}>Use default</Btn>
                  </div>
                </>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: T.soft, letterSpacing: "0.02em" }}>{PASS_COLOR_LABELS[k]}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", border: `1px solid ${T.border}`, borderRadius: 6, background: T.white }}>
                    <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: 4, background: v ?? fallback, border: `1px solid ${T.border}`, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: v ? T.n1 : T.soft, fontFamily: v ? T.fontMono : T.font, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v ?? "Platform default"}</span>
                  </div>
                  {v === null ? (
                    <Btn variant="secondary" size="sm" disabled={!canWrite} onClick={() => { set(k, fallback); setOpenKey(k); }}>Set a colour</Btn>
                  ) : (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Btn variant="secondary" size="sm" disabled={!canWrite} onClick={() => setOpenKey(k)}>Change</Btn>
                      <Btn variant="ghost" size="sm" disabled={!canWrite} onClick={() => set(k, null)}>Use default</Btn>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Advisory only — never a save blocker. The backend does not validate
        * contrast and has no plans to, so a blocking client check would enforce
        * a rule production does not and split design time from production on
        * what is acceptable. */}
      {notice ? (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", borderRadius: 6, background: notice.kind === "defect" ? T.orange2 : T.n6, border: `1px solid ${notice.kind === "defect" ? T.orange1 : T.border}` }}>
          <Icon.warn size={13} />
          <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: T.n2 }}>
            {notice.kind === "defect"
              ? `Low contrast on the event ticket — ${notice.ratio.toFixed(1)}:1 between the label colour and the background, under the ${PASS_CONTRAST_MIN}:1 guideline. That is the pass a guest receives today.`
              : `Label colour is unset, so it falls back to a different default per pass style — on ${PASS_STYLE_NAMES[notice.styleId] ?? notice.styleId} that's ${notice.ratio.toFixed(1)}:1 against your background. Setting a label colour resolves it.`}
          </p>
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {/* Not gated on `dirty`. The hex field commits on blur, and that blur is the
            * same interaction that presses Save — so a render where `dirty` is still
            * false can be the render the click lands in, leaving the button inert at
            * exactly the moment it is needed. `save()` reads the live edits itself and
            * no-ops when nothing changed, which is a harmless click; a dead button at
            * the end of typing a colour is not. */}
          <Btn size="sm" disabled={!canWrite || saving} onClick={() => { void save(); }}>{saving ? "Saving…" : "Save appearance"}</Btn>
        {dirty ? <Btn variant="ghost" size="sm" disabled={saving} onClick={() => { editsRef.current = {}; setEdits({}); }}>Cancel</Btn> : null}
      </div>
    </div>
  );
}
